import React, { useState } from 'react';
import './Products.css';
import './ProductsActions.css';
import './MobileResponsive.css';
import '../FullViewLayout.css';
import './ViewSaleModalStyles.css';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';

const CustomerGroups = ({ isActive, searchTerm, onSearchChange, onShowFilters }) => {
    // Mock data as no API endpoint identified yet
    const [groups, setGroups] = useState([
        { id: 1, name: 'Retail', percentage: '0', selling_price_group: 'Default', status: 'Active' },
        { id: 2, name: 'Wholesale', percentage: '10', selling_price_group: 'Wholesale Price', status: 'Active' },
    ]);

    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [openActionId, setOpenActionId] = useState(null);
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Derived state for search
    const filteredGroups = groups.filter(group => 
        group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.selling_price_group.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort handler
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Get sorted groups
    const getSortedGroups = (list) => {
        if (!sortConfig.key) return list;
        return [...list].sort((a, b) => {
            let aVal = a[sortConfig.key] || '';
            let bVal = b[sortConfig.key] || '';
            if (typeof aVal === 'string') aVal = aVal.toLowerCase();
            if (typeof bVal === 'string') bVal = bVal.toLowerCase();
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    // Calculate pagination values
    const totalEntries = filteredGroups.length;
    const lastPage = Math.ceil(totalEntries / perPage);
    const sortedGroups = getSortedGroups(filteredGroups);
    const paginatedGroups = sortedGroups.slice((currentPage - 1) * perPage, currentPage * perPage);

    const toggleActionMenu = (id) => {
        setOpenActionId(openActionId === id ? null : id);
    };

    const handleViewGroup = (group) => {
        if (!group) return;
        setSelectedGroup(group);
        setShowViewModal(true);
        setOpenActionId(null);
    };

    const handleCloseModal = () => {
        setShowViewModal(false);
        setSelectedGroup(null);
    };

    // Close dropdowns when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.action-dropdown-container') && 
                !event.target.closest('.export-dropdown-container')) {
                setOpenActionId(null);
                setShowExportDropdown(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const handleExport = (format) => {
        if (!filteredGroups || filteredGroups.length === 0) {
            alert('No data to export');
            return;
        }

        const headers = ['Customer Group Name', 'Calculation Percentage (%)', 'Selling Price Group'];
        const rows = filteredGroups.map(g => [
            g.name || '', g.percentage || '0', g.selling_price_group || 'Default'
        ]);

        const title = 'Customer Groups List';
        const filename = 'customer_groups';

        if (format === 'CSV') exportToCSV(headers, rows, filename);
        else if (format === 'Excel') exportToExcel(headers, rows, filename);
        else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
        else if (format === 'Print') printPage();
    };

    return (
        <div className="full-view-inner">
            {/* Table Controls */}
            <div className="table-controls">
                <div className="table-controls-left">
                    <div className="entries-dropdown">
                        <span>Show</span>
                        <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                        <span>entries</span>
                    </div>
                </div>

                <div className="table-controls-right">
                    <div className="tabs-search-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                            type="text"
                            placeholder="Search Customer Groups..."
                            value={searchTerm}
                            onChange={(e) => { onSearchChange(e.target.value); setCurrentPage(1); }}
                            className="tabs-search-input"
                        />
                        <button
                            className="filter-toggle-btn"
                            onClick={onShowFilters}
                            title="Open Filters"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                            </svg>
                            Filters
                        </button>
                    </div>
                    <div className="export-dropdown-container" style={{ position: 'relative' }}>
                        <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                            Export ▾
                        </button>
                        {showExportDropdown && (
                            <div className="export-dropdown-menu">
                                <button onClick={() => { handleExport('CSV'); setShowExportDropdown(false); }}><i className="fa fa-file-csv"></i> Export CSV</button>
                                <button onClick={() => { handleExport('Excel'); setShowExportDropdown(false); }}><i className="fa fa-file-excel"></i> Export Excel</button>
                                <button onClick={() => { handleExport('Print'); setShowExportDropdown(false); }}><i className="fa fa-print"></i> Print</button>
                                <button onClick={() => { handleExport('PDF'); setShowExportDropdown(false); }}><i className="fa fa-file-pdf"></i> Export PDF</button>
                            </div>
                        )}
                    </div>
                    <button className="control-btn-primary" onClick={() => console.log('Add Group')}>
                        <span className="plus-icon">+</span> Add
                    </button>
                </div>
            </div>

            <div className="products-table-container">
                    <table className="products-table">
                     <thead>
                        <tr>
                            <th>Action</th>
                            {[
                                { key: 'name', label: 'Customer Group Name' },
                                { key: 'percentage', label: 'Calculation Percentage (%)' },
                                { key: 'selling_price_group', label: 'Selling Price Group' }
                            ].map(({ key, label }) => (
                                <th key={key} className="sortable" onClick={() => handleSort(key)} style={{ cursor: 'pointer' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                                        {label}
                                        {sortConfig.key === key ? (
                                            sortConfig.direction === 'asc' ? (
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M18 15l-6-6-6 6" />
                                                </svg>
                                            ) : (
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M6 9l6 6 6-6" />
                                                </svg>
                                            )
                                        ) : (
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M7 15l5 5 5-5" />
                                                <path d="M7 9l5-5 5 5" />
                                            </svg>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedGroups.length === 0 ? (
                            <tr><td colSpan="4" style={{ textAlign: 'center', padding: '2rem' }}>No customer groups found.</td></tr>
                        ) : (
                            paginatedGroups.map(group => (
                                <tr key={group.id}>
                                    <td className="actions-cell">
                                        <div className="action-dropdown-container">
                                            <button className="action-btn-trigger" onClick={() => toggleActionMenu(group.id)}>
                                                Actions <span className="arrow-down">▼</span>
                                            </button>
                                            {openActionId === group.id && (
                                                <div className="action-dropdown-menu">
                                                    <button onClick={() => handleViewGroup(group)}><i className="fa fa-eye" style={{ marginRight: '0.5rem', color: '#3b82f6' }}></i> View</button>
                                                    <button onClick={() => console.log('Edit', group.id)}><i className="fa fa-edit" style={{ marginRight: '0.5rem', color: '#10b981' }}></i> Edit</button>
                                                    <button onClick={() => console.log('Delete', group.id)}><i className="fa fa-trash" style={{ marginRight: '0.5rem', color: '#ef4444' }}></i> Delete</button>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td>{group.name}</td>
                                    <td>{group.percentage}%</td>
                                    <td>{group.selling_price_group}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            {filteredGroups.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
                    <div>
                        Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalEntries)} of {totalEntries} entries
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                        <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem' }}>
                            Page {currentPage} of {lastPage || 1}
                        </span>
                        <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(p => Math.min(lastPage, p + 1))}
                            disabled={currentPage === lastPage}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* View Customer Group Modal */}
            {showViewModal && selectedGroup && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-container invoice-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>View Customer Group</h2>
                            <button className="modal-close-btn" onClick={handleCloseModal}>✕</button>
                        </div>
                        <div className="modal-content invoice-content" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                {/* Main info row */}
                                <div className="customer-service-section">
                                    <div className="customer-info-box">
                                        <h3 style={{ fontSize: '1.05rem', fontWeight: '600', marginBottom: '0.75rem', borderBottom: '1px solid var(--etpos-border)', paddingBottom: '0.25rem', color: 'var(--etpos-primary)' }}>Group details</h3>
                                        <div className="info-item">
                                            <strong>Customer Group Name</strong>
                                            <div>{selectedGroup.name || '--'}</div>
                                        </div>
                                        <div className="info-item" style={{ marginTop: '0.5rem' }}>
                                            <strong>Calculation Percentage</strong>
                                            <div>{selectedGroup.percentage || '0'}%</div>
                                        </div>
                                        <div className="info-item" style={{ marginTop: '0.5rem' }}>
                                            <strong>Selling Price Group</strong>
                                            <div>{selectedGroup.selling_price_group || 'N/A'}</div>
                                        </div>
                                    </div>
                                    <div className="service-info-box">
                                        <h3 style={{ fontSize: '1.05rem', fontWeight: '600', marginBottom: '0.75rem', borderBottom: '1px solid var(--etpos-border)', paddingBottom: '0.25rem', color: 'var(--etpos-primary)' }}>Account Status</h3>
                                        <div className="info-item">
                                            <strong>Status</strong>
                                            <div>
                                                <span style={{ 
                                                    padding: '0.2rem 0.6rem', 
                                                    borderRadius: '4px', 
                                                    fontSize: '0.75rem', 
                                                    fontWeight: '700',
                                                    backgroundColor: '#dcfce7',
                                                    color: '#166534'
                                                }}>
                                                    {(selectedGroup.status || 'Active').toUpperCase()}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="modal-btn close-btn" onClick={handleCloseModal}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomerGroups;
